import type { ViewQueryNode, ViewQueryRecordNode, ViewQueryTableNode } from '@salescore/core'
import { pipe, Result } from 'effect'

import { getInnerRecordNodePure, getRecordNodeFromRootNodePure } from '../../state/nodeUtility'
import type { AddNewChildRecordLeft } from '../action/addNewRecord/addNewChildRecord'
import { type DeepReadonly, toMutable } from '../types/DeepReadonly'

export function getChildRecordsWithParent(
  rootRecordNode: DeepReadonly<ViewQueryRecordNode>,
  { innerRowIndex, node }: { innerRowIndex: number; node: DeepReadonly<ViewQueryNode> },
): Result.Result<
  {
    parentRecord: ViewQueryRecordNode
    tableNode: ViewQueryTableNode
    recordNodes: ViewQueryRecordNode[]
  },
  AddNewChildRecordLeft
> {
  if (node.write === undefined) {
    return Result.fail({
      message: 'このブロックは書き込み禁止です',
    })
  }

  if (node.write.parentIdColumn === undefined) {
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO: ViewTree上での子ノードが、DB上での親テーブルに相当するときの分岐
    // このケースの場合は、親テーブルのレコードを新規作成した上で、そのIDをViewTree上での親ノードの参照IDに更新すべきだが、現状これが行えないため、なにも行わない形にする
    return Result.fail({
      message: '参照先を新規作成することはできません',
    })
  }

  if (node.path.length === 1) {
    return Result.fail({
      message: 'ルートレコードに親レコードは存在しません',
    })
  }

  const parentLevelRecords = getRecordNodeFromRootNodePure(rootRecordNode, node.path.slice(0, -1))

  if (parentLevelRecords === undefined || (!Array.isArray(parentLevelRecords) && node.path.length !== 2)) {
    return Result.fail({
      message: 'ノードパスが不正です',
    })
  }

  const parentRecord = pipe(getParentRecordPure(parentLevelRecords, innerRowIndex), toMutable)
  if (parentRecord === undefined) {
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO: そもそもこの条件になるセルは書き込み禁止にする
    return Result.fail({
      message: '親レコードが存在しません。先に親レコードを作成してください。',
    })
  }
  const currentNodeName = node.path.at(-1) ?? ''
  const parentTableRecord = parentRecord.children.find((x) => x.nodeName === currentNodeName)
  if (parentTableRecord === undefined) {
    // なかったら作成する
    const newParentTableRecord: ViewQueryTableNode = {
      nodeName: currentNodeName,
      children: [],
    }
    const newParentRecord = {
      ...parentRecord,
      children: [...parentRecord.children, newParentTableRecord],
    }
    return Result.succeed({
      parentRecord: newParentRecord,
      tableNode: newParentTableRecord,
      recordNodes: newParentTableRecord.children,
    })
  }

  return Result.succeed({
    parentRecord,
    tableNode: parentTableRecord,
    recordNodes: parentTableRecord.children,
  })
}

function isDeepReadonlyViewQueryRecordNodeArray(
  value: DeepReadonly<ViewQueryRecordNode> | DeepReadonly<ViewQueryRecordNode[]>,
): value is DeepReadonly<ViewQueryRecordNode[]> {
  return Array.isArray(value)
}

function getParentRecordPure(
  parentLevelRecords: DeepReadonly<ViewQueryRecordNode> | DeepReadonly<ViewQueryRecordNode[]>,
  innerRowIndex: number,
): DeepReadonly<ViewQueryRecordNode> | undefined {
  if (isDeepReadonlyViewQueryRecordNodeArray(parentLevelRecords)) {
    const encompassingParent = getInnerRecordNodePure(parentLevelRecords, innerRowIndex)
    // どの親レコードにも含まれない場合は、一番最後の親レコードを返す（末尾に追加することになるため）
    return encompassingParent ?? parentLevelRecords.at(-1)
  }

  return parentLevelRecords
}
